import { z } from "zod";
import {
  eraseFieldType,
  type ErasableFieldDescriptor,
  type FieldSortKind,
  type RegisteredField,
} from "./types.js";

import { sectionField } from "./section.js";
import { headingField } from "./heading.js";
import { richTextField } from "./richText.js";
import { dividerField } from "./divider.js";
import { spacerField } from "./spacer.js";
import { pageBreakField } from "./pageBreak.js";
import { imageField } from "./image.js";
import { videoEmbedField } from "./videoEmbed.js";

import { nameField } from "./name.js";
import { emailField } from "./email.js";
import { phoneField } from "./phone.js";
import { addressField } from "./address.js";
import { websiteField } from "./website.js";

import { textSingleField } from "./textSingle.js";
import { textMultiField } from "./textMulti.js";
import { textRegexField } from "./textRegex.js";

import { numberField } from "./numberField.js";
import { decimalField } from "./decimal.js";
import { currencyField } from "./currency.js";
import { formulaField } from "./formula.js";

import { dropdownField } from "./dropdown.js";
import { radioField } from "./radio.js";
import { checkboxField } from "./checkbox.js";
import { multiSelectField } from "./multiSelect.js";
import { imageChoiceField } from "./imageChoice.js";
import { largeListField } from "./largeList.js";

import { matrixRadioField } from "./matrixRadio.js";
import { matrixCheckboxField } from "./matrixCheckbox.js";

import { dateField } from "./date.js";
import { timeField } from "./time.js";
import { datetimeField } from "./datetime.js";
import { monthYearField } from "./monthYear.js";

import { ratingField } from "./rating.js";
import { sliderField } from "./slider.js";
import { npsField } from "./nps.js";

import { termsField } from "./terms.js";
import { decisionBoxField } from "./decisionBox.js";
import { yesNoField } from "./yesNo.js";

import { uniqueIdField } from "./uniqueId.js";
import { randomIdField } from "./randomId.js";

import { hiddenField } from "./hidden.js";
import { utmCaptureField } from "./utmCapture.js";
import { geoCaptureField } from "./geoCapture.js";

import { ngStateLgaField } from "./ngStateLga.js";

/**
 * The authoritative field-type registry — every type in docs/01-PRODUCT-SPEC.md §3.2, in palette
 * order. `GET /field-types` serves this, so the builder palette is server-driven and can never
 * drift from what the validator accepts (CLAUDE.md: hardcoding a field list in the UI instead of
 * reading the registry is listed as a change that gets rejected).
 */
const ALL_DESCRIPTORS = [
  // Layout & content
  sectionField,
  headingField,
  richTextField,
  dividerField,
  spacerField,
  pageBreakField,
  imageField,
  videoEmbedField,
  // Basic info
  nameField,
  emailField,
  phoneField,
  addressField,
  websiteField,
  // Text
  textSingleField,
  textMultiField,
  textRegexField,
  // Number
  numberField,
  decimalField,
  currencyField,
  formulaField,
  // Choice
  dropdownField,
  radioField,
  checkboxField,
  multiSelectField,
  imageChoiceField,
  largeListField,
  // Matrix
  matrixRadioField,
  matrixCheckboxField,
  // Date & time
  dateField,
  timeField,
  datetimeField,
  monthYearField,
  // Rating
  ratingField,
  sliderField,
  npsField,
  // Legal & consent
  termsField,
  decisionBoxField,
  yesNoField,
  // Identifiers
  uniqueIdField,
  randomIdField,
  // System / hidden
  hiddenField,
  utmCaptureField,
  geoCaptureField,
  // Cascading lookups
  ngStateLgaField,
  // No `satisfies FieldDescriptor<...>[]` here: TConfig appears in both covariant
  // (`defaultConfig`) and contravariant (`valueSchema`) positions, so no single type argument
  // accepts every descriptor. Each module already types its own descriptor against
  // `FieldDescriptor<TConfig>`, which is where the contract is actually enforced.
] as const;

export type RegisteredFieldType = (typeof ALL_DESCRIPTORS)[number]["type"];

export const FIELD_REGISTRY: ReadonlyMap<string, RegisteredField> = new Map(
  ALL_DESCRIPTORS.map((descriptor) => [
    descriptor.type,
    // The one cast in the field system — see ErasableFieldDescriptor for why it is unavoidable
    // and why it is safe. Each descriptor is already type-checked against FieldDescriptor<TConfig>
    // at its own definition site, which is where the real contract is enforced.
    eraseFieldType(descriptor as unknown as ErasableFieldDescriptor),
  ]),
);

export const FIELD_TYPES: readonly string[] = ALL_DESCRIPTORS.map((d) => d.type);

export const getFieldDescriptor = (type: string): RegisteredField | undefined =>
  FIELD_REGISTRY.get(type);

export const isRegisteredFieldType = (type: string): boolean => FIELD_REGISTRY.has(type);

/**
 * The keys a composite field's answer is stored under — `name` → `firstName`, `lastName`, …
 *
 * Read from the descriptor rather than listed here, because a list here is exactly what went wrong:
 * the CSV export carried its own hand-written map saying a name was stored under `first`/`last`,
 * which it never was, and so every applicant's name exported as four empty columns. The descriptor
 * cannot drift from itself.
 *
 * `profileParts` is the right source because it already enumerates the parts — it exists to map
 * each one onto a profile attribute — and a composite that binds no profile attribute (a matrix,
 * whose parts come from its own config rather than its type) correctly reports none, which leaves
 * it as the single joined column it has always been.
 */
export const compositePartsForType = (type: string): readonly string[] => {
  const descriptor = FIELD_REGISTRY.get(type);
  if (!descriptor?.renderHints.isComposite) return [];
  return Object.keys(descriptor.profileParts ?? {});
};

/**
 * A part key as a person reads it: `firstName` → "first name", `postcode` → "postcode".
 *
 * Only ever used to build a column header beside the field's own label, so it stays lowercase and
 * lets the label carry the capital.
 */
export const compositePartLabel = (part: string): string =>
  part
    .replaceAll(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replaceAll("_", " ")
    .toLowerCase();

/**
 * How a field's answers order in a sort — the one place anything outside the registry asks.
 *
 * An unknown type sorts as text rather than throwing: `FormField.type` is a plain String column so
 * that adding a type needs no migration, which means a submission written by a newer deploy can be
 * read back by an older one. Sorting it as text is wrong in the way a missing decimal point is
 * wrong; refusing to draw the table is wrong in a way a coordinator cannot work around.
 */
export const sortKindForFieldType = (type: string): FieldSortKind =>
  FIELD_REGISTRY.get(type)?.sortKind ?? "text";

/**
 * Unwraps `.default()` / `.optional()` / `.nullable()` to find an enum underneath, and returns its
 * allowed values. Derived from `configSchema` rather than hand-listed per field, so a builder
 * dropdown can never drift from what the Zod schema (and therefore the server) actually accepts —
 * the same failure mode CLAUDE.md calls out for a hardcoded field-type list, one level down.
 */
const enumOptionsIn = (schema: z.ZodTypeAny): readonly string[] | null => {
  if (schema instanceof z.ZodEnum) return schema.options;
  if (
    schema instanceof z.ZodDefault ||
    schema instanceof z.ZodOptional ||
    schema instanceof z.ZodNullable
  ) {
    return enumOptionsIn(schema._def.innerType as z.ZodTypeAny);
  }
  return null;
};

/** Every enum-typed config key on a descriptor, keyed by config key. Empty if it has none. */
const configOptionsFor = (configSchema: z.ZodTypeAny): Record<string, readonly string[]> => {
  if (!(configSchema instanceof z.ZodObject)) return {};
  const options: Record<string, readonly string[]> = {};
  for (const [key, fieldSchema] of Object.entries(
    configSchema.shape as Record<string, z.ZodTypeAny>,
  )) {
    const values = enumOptionsIn(fieldSchema);
    if (values) options[key] = values;
  }
  return options;
};

/** The palette payload for `GET /field-types`. Zod schemas are not serialisable, so they are omitted. */
export interface FieldTypeSummary {
  type: string;
  label: string;
  category: string;
  icon: string;
  defaultConfig: Record<string, unknown>;
  supportsProfileBinding: boolean;
  defaultSearchable: boolean;
  renderHints: Record<string, unknown>;
  /** Config keys whose value must be one of a fixed set — e.g. `align: ["left","center","right"]`.
   *  Lets the builder render a `<select>` instead of free text without shipping the Zod schema. */
  configOptions: Record<string, readonly string[]>;
}

export const fieldTypeSummaries = (): FieldTypeSummary[] =>
  ALL_DESCRIPTORS.map((descriptor) => ({
    type: descriptor.type,
    label: descriptor.label,
    category: descriptor.category,
    icon: descriptor.icon,
    defaultConfig: descriptor.defaultConfig as Record<string, unknown>,
    supportsProfileBinding: descriptor.supportsProfileBinding,
    defaultSearchable: descriptor.defaultSearchable,
    renderHints: descriptor.renderHints as Record<string, unknown>,
    configOptions: configOptionsFor(descriptor.configSchema as z.ZodTypeAny),
  }));

/**
 * Splits one field's answer into the profile attributes it should populate.
 *
 * A scalar field contributes its own value under its bound attribute. A composite one contributes
 * a pair per part it actually answered — so a `name` field populates `person.first_name` and
 * `person.last_name` separately, and next year's form can prefill them into two plain text inputs
 * that know nothing about the `name` type.
 *
 * `boundKey` is the field's `profileAttributeKey`; a composite ignores it, since its own mapping
 * is what determines where the parts belong.
 */
export const profileAttributePairs = (
  type: string,
  boundKey: string | null,
  value: unknown,
): { attributeKey: string; value: unknown }[] => {
  const descriptor = getFieldDescriptor(type);
  const parts = descriptor?.profileParts;

  if (!parts) {
    if (!boundKey) return [];
    return [{ attributeKey: boundKey, value }];
  }

  if (typeof value !== "object" || value === null || Array.isArray(value)) return [];
  const record = value as Record<string, unknown>;

  const pairs: { attributeKey: string; value: unknown }[] = [];
  for (const [part, attributeKey] of Object.entries(parts)) {
    const partValue = record[part];
    // An unanswered part must not blank an attribute another form filled in.
    if (partValue === undefined || partValue === null || partValue === "") continue;
    pairs.push({ attributeKey, value: partValue });
  }
  return pairs;
};

/**
 * The inverse: rebuilds a composite answer from stored profile attributes for prefill. Returns
 * undefined when nothing is known, so the caller can tell "no data" from "an empty object".
 */
export const profileValueForField = (
  type: string,
  boundKey: string | null,
  read: (attributeKey: string) => unknown,
): unknown => {
  const descriptor = getFieldDescriptor(type);
  const parts = descriptor?.profileParts;

  if (!parts) return boundKey ? read(boundKey) : undefined;

  const assembled: Record<string, unknown> = {};
  for (const [part, attributeKey] of Object.entries(parts)) {
    const stored = read(attributeKey);
    if (stored === undefined || stored === null || stored === "") continue;
    assembled[part] = stored;
  }
  return Object.keys(assembled).length > 0 ? assembled : undefined;
};
